// The buildings' fades: the client half of `BuildingMan` (BuildingMan.cpp: `GraphicUpdate`
// 0xbfbab0 with `ResetBuildingKeysGraphical` 0xbfa4b0, `UpdateTimers` 0xbfa8d0 and
// `UpdateVisibilityGraphical` 0xbfaf90) and of `WS_BuildingManager::Update` 0xdef2b0 /
// `WS_Building::Update` 0xe1bef0, which the sim does not run (docs/platform/RenderFidelity.md
// "Building fades"). A visible worm of the active alliance (or a payload it can see) entering a
// building no ally holds opens it (`OnBuildingRevealed` → `WS_BuildingManager::Unlock(key,
// true)`); the last one leaving schedules the hide after `BuildingLeftTimeoutSecs`. Every frame
// each building's fade level moves by `m_fBuildingFadeSpeed` towards 0 (open) or 1 (closed),
// snapping below 0.03 / above 0.97, and the FG landscape shader multiplies the alpha of the
// building's FG pixels by it (`buildingFades[key]`, copied before the move).
//
// Input: the landscape records of every sim step (crates/sim/src/view/landscape.rs), applied in
// order. Not ported: the worm labels (`SetLabelVisible`), the `TallyMan` worm events and the
// network rule's local worms/teams (`NetSetup::IsLocalWorm`/`IsLocalTeam`: the caller passes
// them, offline nothing is local).

const MAX_WORMS = 48;
const MAX_PAYLOADS = 64;
/** `WS_Building::Update`'s snaps (.rodata 0x15d1ad8, 0x15d1adc). */
const SNAP_CLOSED = Math.fround(0.97), SNAP_OPEN = Math.fround(0.03);

type WormIn = { present: boolean; team: number; alliance: number; dead: boolean; key: number };
type PayloadIn = { has: boolean; vision: number; giblet: boolean; key: number };
type GraphicIn = {
  tTime: number; flags: number; team: number; active: number; teamAI: boolean; activeHuman: boolean; timeout: number;
  teams: { alliance: number; ai: boolean }[]; humans: boolean[]; worms: WormIn[];
  nPayloads: number; dropped: Uint32Array; payloads: PayloadIn[];
};
type Building = { key: number; open: boolean; fade: number };

class Reader {
  p = 0;
  private dv: DataView;
  constructor(dv: DataView) { this.dv = dv; }
  u8(): number { return this.dv.getUint8(this.p++); }
  u16(): number { const v = this.dv.getUint16(this.p, true); this.p += 2; return v; }
  u32(): number { const v = this.dv.getUint32(this.p, true); this.p += 4; return v; }
  f32(): number { const v = this.dv.getFloat32(this.p, true); this.p += 4; return v; }
  get done(): boolean { return this.p >= this.dv.byteLength; }
}

/** One active girder of `WS_DestructionManager::m_GIRDERS_List` (the `XGIRDER_SUB` copy). */
export type GirderView = {
  pos: [number, number, number]; dims: [number, number]; add: [number, number, number, number];
  angle: number; type: number; status: number; lastUpdate: number;
};

export class Buildings {
  /** `buildingFades[15]` of the FG landscape geomset, by IO key. */
  readonly fades = new Float32Array(15).fill(1);
  /** `WS_GeoSet::m_StandardSoilEnabled` (`!m_bIsFortMode`). */
  soilEnabled = true;
  /** The girders of the last `WS_DestructionManager::Update` (the girder geomset's list). */
  girders: GirderView[] = [];
  /** `NetSetup::IsNetGameRunning` and the local worms / teams of a network match (offline: no
   * net game). */
  net: { running: boolean; localTeam(t: number): boolean } = { running: false, localTeam: () => false };

  // BuildingMan::GraphicalDataClass m_Gd and the graphical members.
  private wormKeys = new Uint32Array(MAX_WORMS);
  private resetPending = false;
  private lockBuildings = false;
  private leftKey = 0;
  private leftExpiry = 0;
  private pairH = new Uint32Array(MAX_PAYLOADS);
  private pairK = new Uint32Array(MAX_PAYLOADS);
  private wormAlliance = new Uint32Array(MAX_WORMS);
  private uWormKey = new Uint32Array(MAX_WORMS);
  private numWormsInGame = 0;
  private nextWorm = 0;
  /** The latest worm record of the step (the cached `m_currentWorms` read through). */
  private worms: WormIn[] = [];
  /** `WS_BuildingManager`'s buildings by key (fade level, open). */
  private buildings = new Map<number, Building>();

  /** One step's landscape records (crates/sim/src/view/landscape.rs), in call order. */
  step(bytes: Uint8Array): void {
    if (!bytes.length) return;
    const r = new Reader(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength));
    while (!r.done) {
      const tag = String.fromCharCode(r.u8());
      if (tag === 'R') this.resetBuildingKeys(r.u8() !== 0);
      else if (tag === 'U') { const key = r.u32(); this.unlock(key, r.u8() !== 0); }
      else if (tag === 'G') this.graphicUpdate(readGraphic(r));
      else if (tag === 'F') this.managerUpdate(r);
      else if (tag === 'W') this.girders = readGirders(r);
      else throw new Error(`landscape record: bad tag ${tag}`);
    }
  }

  /** `BuildingMan::ResetBuildingKeys(bLockBuildings)` 0xbfa450 (the logical call). */
  private resetBuildingKeys(lock: boolean): void {
    if (lock) this.lockBuildings = true;
    this.resetPending = true;
  }

  /** `WS_BuildingManager::Unlock(key, bUnlocked)` 0xdef490. */
  private unlock(key: number, open: boolean): void {
    const b = this.buildings.get(key);
    if (key !== 0 && b) b.open = open;
  }

  /** `BuildingMan::GraphicUpdate(tTime)` 0xbfbab0. */
  private graphicUpdate(g: GraphicIn): void {
    this.worms = g.worms;
    const updateAll = this.resetPending;
    this.resetBuildingKeysGraphical();
    if (g.team >>> 0 < 6) {
      this.updateTimers(g);
      this.updateVisibilityGraphical(g, updateAll);
    }
  }

  /** `BuildingMan::ResetBuildingKeysGraphical()` 0xbfa4b0. */
  private resetBuildingKeysGraphical(): void {
    if (!this.resetPending) return;
    this.resetPending = false;
    for (let i = 0; i < MAX_WORMS; i++) {
      if (this.wormKeys[i] !== 0 && this.lockBuildings) this.unlock(this.wormKeys[i], false);
      this.wormKeys[i] = 0;
    }
    if (this.lockBuildings && this.leftKey !== 0) this.unlock(this.leftKey, false);
    this.leftExpiry = 0;
    this.leftKey = 0;
    for (let i = 0; i < MAX_PAYLOADS; i++) {
      if (this.lockBuildings && this.pairK[i] !== 0) this.unlock(this.pairK[i], false);
      this.pairH[i] = 0;
      this.pairK[i] = 0;
    }
    this.lockBuildings = false;
  }

  /** `BuildingMan::UpdateTimers(tTime)` 0xbfa8d0 (its `HasAlliesInBuildingGraphical` result is
   * unused). */
  private updateTimers(g: GraphicIn): void {
    if (this.leftExpiry !== 0 && this.leftKey !== 0 && this.leftExpiry <= g.tTime) this.onBuildingHidden(this.leftKey);
  }

  /** `BuildingMan::OnBuildingRevealed(key)` 0xbfa730 (the labels left out). */
  private onBuildingRevealed(key: number): void {
    if (key === this.leftKey) {
      this.leftKey = 0;
      this.leftExpiry = 0;
    }
    this.unlock(key, true);
  }

  /** `BuildingMan::OnBuildingHidden(key)` 0xbfa610 (the labels left out). */
  private onBuildingHidden(key: number): void {
    this.unlock(key, false);
    this.leftKey = 0;
    this.leftExpiry = 0;
  }

  /** `BuildingMan::ScheduleBuildingHidden(tTime, key)` 0xbfa6d0. */
  private scheduleBuildingHidden(tTime: number, key: number, timeout: number): void {
    const left = this.leftKey;
    if (left !== 0 && left !== key) {
      this.onBuildingHidden(left);
      this.leftKey = key;
      this.leftExpiry = tTime;
      return;
    }
    this.leftKey = key;
    this.leftExpiry = Math.fround(tTime + timeout);
  }

  /** `BuildingMan::HasAlliesInBuildingGraphical(alliance, key)` 0xbfa800: a payload pair or a
   * worm of the alliance (slot 67) with that graphical key. */
  private hasAllies(alliance: number, key: number): boolean {
    if (key === 0) return false;
    for (let i = 0; i < MAX_PAYLOADS; i++) if (this.pairK[i] === key) return true;
    for (let i = 0; i < this.worms.length; i++) {
      const k = this.wormKeys[i];
      if (k !== 0 && this.worms[i].alliance === alliance && k === key) return true;
    }
    return false;
  }

  /** `BuildingMan::IsWormVisible_Graphical` 0xbfa300 (dead: slot 79; the AI rule reads the
   * worm's live team, slot 65). */
  private isWormVisible(g: GraphicIn, i: number, wormAlliance: number): boolean {
    const w = this.worms[i];
    if (!w || w.dead) return false;
    if ((g.flags & 8) !== 0 && this.net.running) return this.net.localTeam(w.team);
    if (g.flags & 16) {
      if (g.active === wormAlliance && g.activeHuman) return true;
    } else if (wormAlliance >>> 0 < 6 && g.humans[wormAlliance]) return true;
    if (g.team !== 0xffffffff && g.teamAI) return !(w.team >>> 0 < 6 && g.teams[w.team].ai);
    return false;
  }

  /** `BuildingMan::IsPayloadVisible` 0xbfa3f0. */
  private isPayloadVisible(g: GraphicIn, team: number): boolean {
    if ((g.flags & 8) !== 0 && this.net.running) return this.net.localTeam(team);
    if (!g.activeHuman) return false;
    return g.active === (team >>> 0 < 6 ? g.teams[team].alliance : 0xffffffff);
  }

  /** `BuildingMan::UpdateVisibilityGraphical(tTime, bUpdateAll)` 0xbfaf90 (labels and TallyMan
   * events left out). */
  private updateVisibilityGraphical(g: GraphicIn, updateAll: boolean): void {
    const intro = (g.flags & 2) !== 0, inTurn = (g.flags & 4) !== 0;
    if ((g.flags & 1) || (!intro && (!inTurn || g.team >>> 0 > 5))) return;
    // `m_currentWorms`, `m_wormTeam`, `m_wormAlliance` are cached once per BuildingMan
    // (`m_numWormsInGame == 0`); the client also re-caches when the worm count changes (a new
    // round's BuildingMan). The visible-building list only drives the labels.
    if (this.numWormsInGame === 0 || this.numWormsInGame !== Math.min(g.worms.length, MAX_WORMS)) {
      const n = Math.min(g.worms.length, MAX_WORMS);
      for (let i = 0; i < n; i++) {
        this.uWormKey[i] = 0;
        const t = g.worms[i].team;
        this.wormAlliance[i] = t >>> 0 < 6 ? g.teams[t].alliance : 0xffffffff;
      }
      this.nextWorm = 0;
      this.numWormsInGame = n;
    }
    const active = g.active;
    const n = this.numWormsInGame;
    const visible: boolean[] = [];
    let processed = false;
    for (let i = 0; i < n; i++) {
      const vis = this.isWormVisible(g, i, this.wormAlliance[i]);
      visible[i] = vis;
      if (this.nextWorm === i && !processed) {
        processed = true;
        this.uWormKey[i] = g.worms[i].key;
      }
    }
    for (let i = 0; i < n; i++) {
      if (!updateAll && this.nextWorm !== i) continue;
      if (!visible[i]) continue;
      const newKey = this.uWormKey[i];
      const alliance = this.wormAlliance[i];
      const oldKey = this.wormKeys[i];
      if (newKey === oldKey) continue;
      const hadAllies = this.hasAllies(alliance, newKey);
      this.wormKeys[i] = newKey;
      if (oldKey !== 0 && !this.hasAllies(alliance, oldKey)) this.scheduleBuildingHidden(g.tTime, oldKey, g.timeout);
      if (newKey !== 0 && !hadAllies) this.onBuildingRevealed(newKey);
    }
    if (!updateAll && n !== 0) this.nextWorm = (this.nextWorm + 1) % n;
    this.updatePayloads(g, active);
  }

  /** The payload half of `UpdateVisibilityGraphical`: the stored pairs whose handle no longer
   * matches `GetPayloadDroppedAtIndex` of their index are dropped (their building scheduled
   * hidden without allies) with the following pairs moved down, as the original's loop does it;
   * then every active payload's building key is refreshed. */
  private updatePayloads(g: GraphicIn, active: number): void {
    const H = this.pairH, K = this.pairK, n = g.nPayloads;
    // `GetPayloadDroppedAtIndex(i)`: m_ActivePayloads[i], 64 entries.
    const dropped = g.dropped;
    let i = 0, li = 0, shifted = false, label = 'B633';
    for (let guard = 0; guard < 100000; guard++) {
      if (label === 'B633') {
        label = H[li] === 0 ? 'B6B2' : 'DO';
      } else if (label === 'DO') {
        if (shifted) {
          i = i - 1;
          li = i;
          label = n !== 0 ? 'B662' : 'B960';
        } else label = n === 0 ? 'B898' : 'B662';
      } else if (label === 'B662') {
        label = H[li] === dropped[i] ? 'NEXT' : 'B960';
      } else if (label === 'B960') {
        label = H[li] !== 0 ? 'B898' : 'NEXT';
      } else if (label === 'NEXT') {
        shifted = false;
        label = 'NEXT2';
      } else if (label === 'NEXT2') {
        i++;
        if (i > 63) break;
        li = i;
        label = H[li] !== 0 ? 'DO' : 'B6B2';
      } else if (label === 'B6B2') {
        H[li] = 0;
        K[li] = 0;
        label = 'NEXT2';
      } else if (label === 'B898') {
        const key = K[li];
        const allies = this.hasAllies(active, key);
        if (key !== 0 && !allies) this.scheduleBuildingHidden(g.tTime, key, g.timeout);
        i++;
        H[li] = 0;
        K[li] = 0;
        if (i === 64) break;
        li = i;
        let j = i;
        if (H[j] === 0) shifted = false;
        else {
          let th = H[j], tk = K[j];
          do {
            H[j - 1] = th;
            K[j - 1] = tk;
            j++;
            H[j - 1] = 0;
            K[j - 1] = 0;
            if (j === 64) break;
            th = H[j];
            tk = K[j];
          } while (th !== 0);
          shifted = true;
        }
        label = 'B633';
      }
    }
    for (let p = 0; p < n; p++) {
      const handle = dropped[p];
      const oldKey = K[p];
      H[p] = handle;
      const info = g.payloads[p];
      const vision = info?.vision ?? 0xffffffff;
      const newKey = handle === 0 || !info?.has || vision >>> 0 > 5 || !this.isPayloadVisible(g, vision) ? 0 : info.key;
      if (newKey === oldKey) continue;
      if (info?.giblet || this.hasAllies(active, newKey)) {
        K[p] = newKey;
        if (oldKey !== 0 && !this.hasAllies(active, oldKey)) this.scheduleBuildingHidden(g.tTime, oldKey, g.timeout);
      } else {
        K[p] = newKey;
        if (oldKey !== 0 && !this.hasAllies(active, oldKey)) this.scheduleBuildingHidden(g.tTime, oldKey, g.timeout);
        if (newKey !== 0) this.onBuildingRevealed(newKey);
      }
    }
  }

  /** `WS_BuildingManager::Update()` 0xdef2b0: the fade levels of keys 1..7 into the FG geomset's
   * `buildingFades`, then every building's `WS_Building::Update(m_fBuildingFadeSpeed)`. */
  private managerUpdate(r: Reader): void {
    const speed = r.f32();
    this.soilEnabled = r.u8() !== 0;
    const n = r.u16();
    const present: Building[] = [];
    for (let i = 0; i < n; i++) {
      const key = r.u32(), open = r.u8() !== 0, fade = r.f32();
      if (key === 0) continue;
      let b = this.buildings.get(key);
      if (!b) {
        b = { key, open, fade };
        this.buildings.set(key, b);
      }
      present.push(b);
    }
    for (const b of present) if (b.key - 1 >>> 0 < 7) this.fades[b.key] = b.fade;
    for (const b of present) {
      if (!b.open) {
        const f = Math.fround(speed + b.fade);
        b.fade = SNAP_CLOSED < f ? 1 : f;
      } else {
        const f = Math.fround(b.fade - speed);
        b.fade = f < SNAP_OPEN ? 0 : f;
      }
    }
  }
}

function readGraphic(r: Reader): GraphicIn {
  const tTime = r.f32(), flags = r.u8(), team = r.u32(), active = r.u32(), teamAI = r.u8() !== 0, activeHuman = r.u8() !== 0, timeout = r.f32();
  const teams: { alliance: number; ai: boolean }[] = [];
  for (let n = r.u8(), i = 0; i < n; i++) teams.push({ alliance: r.u32(), ai: r.u8() !== 0 });
  const humans: boolean[] = [];
  for (let n = r.u8(), i = 0; i < n; i++) humans.push(r.u8() !== 0);
  const worms: WormIn[] = [];
  for (let n = r.u16(), i = 0; i < n; i++) worms.push({ present: r.u8() !== 0, team: r.u32(), alliance: r.u32(), dead: r.u8() !== 0, key: r.u32() });
  const nPayloads = r.u16();
  const dropped = new Uint32Array(MAX_PAYLOADS);
  for (let i = 0; i < MAX_PAYLOADS; i++) dropped[i] = r.u32();
  const payloads: PayloadIn[] = [];
  for (let i = 0; i < nPayloads; i++) payloads.push({ has: r.u8() !== 0, vision: r.u32(), giblet: r.u8() !== 0, key: r.u32() });
  return { tTime, flags, team, active, teamAI, activeHuman, timeout, teams, humans, worms, nPayloads, dropped, payloads };
}

function readGirders(r: Reader): GirderView[] {
  const out: GirderView[] = [];
  for (let n = r.u16(), i = 0; i < n; i++) {
    const pos: [number, number, number] = [r.f32(), r.f32(), r.f32()];
    const dims: [number, number] = [r.f32(), r.f32()];
    const add: [number, number, number, number] = [r.f32(), r.f32(), r.f32(), r.f32()];
    out.push({ pos, dims, add, angle: r.f32(), type: r.u32(), status: r.u32(), lastUpdate: r.f32() });
  }
  return out;
}
